<template>
  <div>
    <v-row
      v-if="loading"
      justify="center"
      align="center"
      style="min-height:200px"
    >
      <v-progress-circular indeterminate color="primary" />
    </v-row>

    <template v-else>
      <!-- Master toggle -->
      <v-row align="center" class="mb-2">
        <v-col cols="12">
          <v-list-item class="px-0">
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold text-subtitle-1">
                Notificaciones
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption grey--text">
                Activa o desactiva todas las notificaciones
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-switch
                v-model="isActive"
                color="primary"
                inset
                :disabled="saving"
                @change="onToggleActive"
              />
            </v-list-item-action>
          </v-list-item>
          <v-divider />
        </v-col>
      </v-row>

      <!-- Notification types -->
      <v-row>
        <v-col cols="12">
          <div class="mb-3">
            <span
              class="font-weight-medium text-body-2 grey--text text--darken-2"
            >
              Tipos de notificación
            </span>
          </div>
          <v-list dense class="pa-0">
            <v-list-item
              v-for="item in notificationItems"
              :key="item.key"
              class="px-0"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ item.label }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-switch
                  v-model="leadNotifications[item.key]"
                  color="primary"
                  inset
                  dense
                  :disabled="!isActive || saving"
                  @change="val => onToggleNotification(item.key, val)"
                />
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>

      <!-- Delivery method -->
      <v-row class="mt-2">
        <v-col cols="12">
          <v-divider class="mb-4" />
          <div class="mb-3">
            <span
              class="font-weight-medium text-body-2 grey--text text--darken-2"
            >
              Método de entrega
            </span>
          </div>
          <v-chip-group
            v-model="selectedDeliveryMethods"
            multiple
            column
            active-class="primary--text primary lighten-5"
            :disabled="!isActive || saving"
            @change="onDeliveryMethodChange"
          >
            <v-chip
              v-for="method in deliveryMethodOptions"
              :key="method.value"
              :value="method.value"
              filter
              outlined
              :disabled="!isActive || saving"
            >
              <v-icon left small>{{ method.icon }}</v-icon>
              {{ method.label }}
            </v-chip>
          </v-chip-group>
        </v-col>
      </v-row>

      <v-row v-if="errorMessage">
        <v-col cols="12">
          <small class="red--text">{{ errorMessage }}</small>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import {
  getNotificationPreferences,
  createNotificationPreferences,
  updateNotificationPreferences
} from "@/api/notification-api.js";

const DEFAULT_LEAD_NOTIFICATIONS = {
  "new-lead-created": true,
  "lead-assigned": true,
  "leads-reassigned": true,
  "lead-new-comment": true,
  "lead-phase-change": false,
  "lead-reminder": true
};
//{ key: "leads-reassigned", label: "Leads reasignados" },
const NOTIFICATION_ITEMS = [
  { key: "new-lead-created", label: "Nuevo lead creado" },
  { key: "lead-assigned", label: "Lead asignado" },
  { key: "lead-new-comment", label: "Nuevo comentario en un lead" },
  { key: "lead-phase-change", label: "Cambio de fase del lead" },
  { key: "lead-reminder", label: "Recordatorio de lead" }
];

export default {
  name: "NotificationSettings",
  props: {
    userToUpdate: {
      type: Object,
      default: () => null
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      errorMessage: "",
      isActive: true,
      isFirstTime: true,
      leadNotifications: { ...DEFAULT_LEAD_NOTIFICATIONS },
      selectedDeliveryMethods: ["push"],
      notificationItems: NOTIFICATION_ITEMS,
      deliveryMethodOptions: [
        { value: "push", label: "Push", icon: "mdi-bell-outline" },
        { value: "email", label: "Email", icon: "mdi-email-outline" }
      ]
    };
  },
  computed: {
    userId() {
      return this.userToUpdate ? this.userToUpdate._id : null;
    },
    accessGroup() {
      return this.userToUpdate ? this.userToUpdate.real_estate_group_id : null;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadPreferences();
      }
    }
  },
  mounted() {
    if (this.visible) {
      this.loadPreferences();
    }
  },
  methods: {
    async loadPreferences() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const res = await getNotificationPreferences({
          userId: this.userId,
          accessGroup: this.accessGroup
        });

        if (res && res.Code === 200 && res.Data) {
          const data = res.Data;
          this.isActive = data.is_active !== undefined ? data.is_active : true;
          this.leadNotifications = data.lead_notifications
            ? { ...DEFAULT_LEAD_NOTIFICATIONS, ...data.lead_notifications }
            : { ...DEFAULT_LEAD_NOTIFICATIONS };
          this.selectedDeliveryMethods =
            data.delivery_method && data.delivery_method.length
              ? [...data.delivery_method]
              : ["push"];
          this.isFirstTime = res.Message === "Preferencias predeterminadas";
        } else {
          this.isFirstTime = true;
          this.resetToDefaults();
        }
      } catch {
        this.isFirstTime = true;
        this.resetToDefaults();
      } finally {
        this.loading = false;
      }
    },
    resetToDefaults() {
      this.isActive = true;
      this.leadNotifications = { ...DEFAULT_LEAD_NOTIFICATIONS };
      this.selectedDeliveryMethods = ["push"];
    },
    async onToggleActive(val) {
      await this.savePartial({ is_active: val });
    },
    async onToggleNotification(key, val) {
      await this.savePartial({ lead_notifications: { [key]: val } });
    },
    async onDeliveryMethodChange(val) {
      if (!val || val.length === 0) return;
      await this.savePartial({ delivery_method: val });
    },
    async savePartial(partialData) {
      this.saving = true;
      this.errorMessage = "";
      try {
        if (this.isFirstTime) {
          const body = {
            user_id: this.userId,
            lead_notifications: this.leadNotifications,
            delivery_method: this.selectedDeliveryMethods,
            is_active: this.isActive,
            ...partialData
          };
          if (this.accessGroup) body.access_group = this.accessGroup;
          const res = await createNotificationPreferences(body);
          if (res && res.Code === 200) {
            this.isFirstTime = false;
          } else {
            this.errorMessage = "Error al guardar las preferencias.";
          }
        } else {
          const res = await updateNotificationPreferences({
            data: partialData,
            userId: this.userId
          });
          if (!res || res.Code === 404) {
            // Preferences not found, create them
            this.isFirstTime = true;
            await this.savePartial(partialData);
          } else if (res.Code !== 200) {
            this.errorMessage = "Error al actualizar las preferencias.";
          }
        }
      } catch {
        this.errorMessage = "Error al guardar las preferencias.";
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
